import { BrightnessContrast, Close, Logout, Switcher as SwitcherIcon } from "@carbon/icons-react";
import {
  FlexGrid,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderPanel,
  Layer,
  Row,
  Switcher,
  SwitcherDivider,
  SwitcherItem,
} from "@carbon/react";
import { Header, useModal } from "@flaminem/ui-components";
import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import LogoutModal from "src/authentication/LogoutModal.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { useTokenAuth } from "src/authentication/TokenApp.tsx";
import { ModalTransition } from "src/components/Modal";
import ToastContainer from "src/components/Toasts/ToastContainer.tsx";
import { GlobalErrorLayout } from "src/errors/GlobalErrorLayout.tsx";
import { SchemaClientModal } from "src/features/schema/UpdateSchema";
import { Configuration } from "src/http/Configuration.api.ts";
import { useT } from "src/internationalization";
import { LocaleSwitcher } from "src/internationalization/locale/LocaleSwitcher.tsx";
import BrandLogo from "src/pages/Sidebar/BrandLogo.tsx";
import FlaminemLogo from "src/pages/Sidebar/FlaminemLogo.tsx";
import Sidebar from "src/pages/Sidebar/Sidebar.tsx";
import { useTheme } from "src/theme";

const AppLayout: React.FC = () => {
  const { pathname } = useLocation();
  const auth = useTokenAuth();
  const [showMenu, setShowMenu] = useState(true);
  const { isModalOpen: isLogoutModalOpen, openModal: openLogoutModal } = useModal();

  const {
    isModalOpen: isSchemaClientModalOpen,
    openModal: openSchemaClientModal,
    closeModal: closeSchemaClientModal,
  } = useModal();

  const {
    isModalOpen: isUISchemaModalOpen,
    openModal: openUISchemaModal,
    closeModal: closeUISchemaModal,
  } = useModal();

  const {
    isModalOpen: isConfigurableListsModalOpen,
    openModal: openConfigurableListsModal,
    closeModal: closeConfigurableListsModal,
  } = useModal();

  const [isSideNavExpended, setSideNavExpand] = useState(false);

  const { nextTheme } = useTheme();

  const onLogout = async () => {
    auth?.signoutSilent();
    openLogoutModal();
  };
  const t = useT();

  if (isLogoutModalOpen) {
    return (
      <GlobalErrorLayout>
        <LogoutModal
          onConfirm={async () => {
            window.location.href = window.location.origin;
          }}
        />
      </GlobalErrorLayout>
    );
  }

  return (
    <>
      <FlexGrid className="min-w-min">
        <Row>
          <Header aria-label={t("header.wrapper")} className="border-solid">
            <BrandLogo />
            <FlaminemLogo isSelected={!showMenu} onClick={() => setShowMenu(!showMenu)} />
            <div className="mx-5">
              <LocaleSwitcher />
            </div>

            <HeaderGlobalBar>
              <HeaderGlobalAction aria-label={t("header.theme.toggle")} onClick={nextTheme}>
                <BrightnessContrast />
              </HeaderGlobalAction>
              <AuthorizedItem
                requiredPermissions={[
                  { resource: ScopeResource.CONFIGURATION, scopes: [Scope.EDIT] },
                ]}
              >
                <HeaderGlobalAction
                  aria-label={t("header.switcher")}
                  onClick={() => setSideNavExpand(!isSideNavExpended)}
                >
                  {isSideNavExpended ? <Close /> : <SwitcherIcon />}
                </HeaderGlobalAction>
              </AuthorizedItem>
              <HeaderGlobalAction aria-label={t("header.logout")} onClick={onLogout}>
                <Logout />
              </HeaderGlobalAction>
            </HeaderGlobalBar>
            <HeaderPanel
              expanded={isSideNavExpended}
              onHeaderPanelFocus={() => setSideNavExpand(false)}
              href=""
            >
              <Switcher aria-label={t("header.switcher")}>
                <li className="w-full pl-5 mt-5 mb-0 cds--label">
                  {t("header.switcher.settings")}
                </li>
                <SwitcherDivider />
                <SwitcherItem
                  aria-label={t("header.switcher.settings.ui.schema")}
                  onClick={() => openUISchemaModal()}
                >
                  {t("header.switcher.settings.ui.schema")}
                </SwitcherItem>
                <SwitcherItem
                  aria-label={t("header.switcher.settings.client.schema")}
                  onClick={() => openSchemaClientModal()}
                >
                  {t("header.switcher.settings.client.schema")}
                </SwitcherItem>
                <SwitcherItem
                  aria-label={t("header.switcher.settings.conflists")}
                  onClick={() => openConfigurableListsModal()}
                >
                  {t("header.switcher.settings.conflists")}
                </SwitcherItem>
              </Switcher>
            </HeaderPanel>
          </Header>
        </Row>
        <Row>
          <Sidebar pathName={pathname} showMenu={showMenu} />
          <main
            className="cds--content"
            style={{
              marginTop: "2.5rem",
              padding: "1.5rem",
              minHeight: "calc(100vh - 2.5rem)",
              background: "var(--cds-field)",
            }}
          >
            <Layer level={1}>
              <ToastContainer />
              <Outlet />
            </Layer>
          </main>
        </Row>
      </FlexGrid>
      <ModalTransition>
        {isSchemaClientModalOpen && (
          <SchemaClientModal
            onClose={closeSchemaClientModal}
            type={t("header.switcher.settings.client.schema")}
            urlGet={Configuration.Configuration().getClientSchemaUrl()}
            urlUpdate={Configuration.Configuration().putClientSchemaUrl()}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isUISchemaModalOpen && (
          <SchemaClientModal
            onClose={closeUISchemaModal}
            type={t("header.switcher.settings.ui.schema")}
            urlGet={Configuration.Configuration().getUISchemaUrl()}
            urlUpdate={Configuration.Configuration().putUISchemaUrl()}
          />
        )}
      </ModalTransition>
      <ModalTransition>
        {isConfigurableListsModalOpen && (
          <SchemaClientModal
            onClose={closeConfigurableListsModal}
            type={t("header.switcher.settings.conflists")}
            urlGet={Configuration.Configuration().getConfigurableListsUrl()}
            urlUpdate={Configuration.Configuration().putConfigurableListsUrl()}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default AppLayout;
